import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { DeductionType } from "@wingspanhq/payments/dist/interfaces/deductions";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import React from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationCreateCollaboratorDeduction } from "../../../../query/payments/mutations";

type FormData = {
  name: string;
  amount: number;
  startDate: Date;
  detail: string;
};

type Props = {
  clientId: string;
  memberId: string;
} & WSModalCustomProps<void>;

const ModalAddDeduction: React.FC<Props> = ({
  clientId,
  memberId,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [createDeduction, createDeductionMeta] =
    useMutationCreateCollaboratorDeduction();

  return (
    <WSForm<FormData>
      defaultValues={{
        startDate: new Date(),
        name: "",
        detail: ""
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Name is required"),
        detail: Yup.string().required("Detail is required"),
        amount: Yup.number().required("Amount is required"),
        startDate: Yup.date().required("Start date is required")
      })}
      onSubmit={async formData => {
        await createDeduction(
          {
            name: formData.name,
            type: DeductionType.PrePayment,
            amount: formData.amount,
            startDate: formData.startDate,
            detail: formData.detail,
            clientId,
            currency: Currency.USD,
            memberId
          },
          {
            onSuccess() {
              openSnackbar({
                message: "Deduction added",
                type: "success"
              });
              modalProps.onClose();
            }
          }
        );
      }}
    >
      <WSModal
        title="New pre-payment deduction"
        size="S"
        actions={{
          alignment: "fill",
          buttons: [
            {
              label: "Add new deduction",
              loading: createDeductionMeta.isLoading
            }
          ]
        }}
        {...modalProps}
      >
        <WSList gap="XL">
          <WSForm.Field
            label="Deduction name"
            name="name"
            component={WSInputText}
          />
          <WSForm.Field
            label="Deduction amount"
            name="amount"
            component={WSInputNumber}
            componentProps={{
              currency: Currency.USD,
              maxFractionDigits: 2
            }}
          />
          <WSForm.Field
            label="Start date"
            name="startDate"
            component={WSInputDate}
          />

          <WSForm.Field
            label="Deduction detail"
            name="detail"
            component={WSInputTextarea}
          />
          <WSErrorMessage
            error={createDeductionMeta.error}
            contextKey="Other"
          />
          <WSText color="gray500">
            Scheduled deductions are completed when you pay a contractor. If a
            deduction is larger than a payable (or your specified percentage of
            the payable) the remaining balance will be deducted from the next
            payable.
          </WSText>
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalAddDeduction = () => useWSModalCustom(ModalAddDeduction);
